import type { AdmissionsAIContext } from "./types";
import { resolveAssistantArtifactSupport } from "../../shared/assistant-artifact-support";
import { isSpreadsheetModificationPrompt } from "../../shared/assistant-spreadsheet-operations";
import {
  ADMISSIONS_THREEJS_VISUALIZATION_PROMPT,
  isTuitionPriorYearVisualizationPrompt,
} from "../../shared/assistant-visualization-prompts";

export type AssistantTaskClass =
  | "artifact_generate"
  | "compute_report"
  | "draft_then_approve"
  | "external_side_effect";

export type AssistantTaskProposal = {
  workflowKey: string;
  taskClass: AssistantTaskClass;
  title: string;
  summary: string;
  autoStart?: boolean;
  recipientEmail?: string;
  familyId?: string;
  academicYear?: string;
  requiresFollowupConfirmation?: boolean;
  disabledReason?: string;
};

const toText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const extractEmail = (question: string): string | undefined => {
  const match = question.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return match?.[0]?.trim();
};

const extractFamilyIdFromPath = (pathname: string): string | undefined => {
  const familyMatch = pathname.match(/\/family\/([^/]+)/i);
  if (familyMatch?.[1]) {
    return familyMatch[1];
  }
  return undefined;
};

const normalizeAcademicYear = (
  context: AdmissionsAIContext,
): string | undefined => {
  const explicit = toText((context as Record<string, unknown>).academicYear);
  if (explicit) {
    return explicit;
  }
  const now = new Date();
  const year = now.getFullYear();
  return `${year}-${year + 1}`;
};

const CHART_EXPLICIT_PATTERN =
  /\b(chart|graph|plot|recharts|bar chart|line chart|area chart|trend line)\b/i;
const CHART_ANALYSIS_PATTERN =
  /\b(compare|comparison|break\s*down|breakdown|distribution|trend|trending|mix|visual(?:ly|ize|ization)?|pattern|patterns)\b/i;
const CHART_GROUPING_PATTERN =
  /\b(show me by|by [a-z0-9-]+|per [a-z0-9-]+|across [a-z0-9-]+)\b/i;
const SPREADSHEET_EXPLICIT_PATTERN =
  /\b(spreadsheet|csv|excel|workbook|sheet export|export sheet)\b/i;
const SPREADSHEET_EXPORT_PATTERN = /\b(export|download|csv|excel)\b/i;
const SPREADSHEET_HANDOFF_PATTERN =
  /\b(offline handoff|handoff|offline review|tabular|row[-\s]?by[-\s]?row|one row per|row[-\s]?level|reconcile|reconciliation|audit|columns?|line items?)\b/i;
const SPREADSHEET_ACTION_PATTERN =
  /\b(package|packaged|deliver|send|audit|review|reconcile|handoff|offline)\b/i;
const THREEJS_VISUALIZATION_PATTERN =
  /\b(threejs|three\.js|3d|advanced visualization|interactive space)\b/i;
const ADMISSIONS_CONTEXT_PATTERN =
  /\b(admissions?|applicants?|applications?|enrollment|pipeline)\b/i;

const inferArtifactWorkflowKey = (
  question: string,
):
  | "artifact.chart.generate"
  | "artifact.spreadsheet.generate"
  | "artifact.visualization3d.generate"
  | null => {
  const normalized = question.toLowerCase();
  if (
    normalized === ADMISSIONS_THREEJS_VISUALIZATION_PROMPT.toLowerCase() ||
    (THREEJS_VISUALIZATION_PATTERN.test(normalized) &&
      ADMISSIONS_CONTEXT_PATTERN.test(normalized)) ||
    isTuitionPriorYearVisualizationPrompt(question)
  ) {
    return "artifact.visualization3d.generate";
  }
  const explicitChart = CHART_EXPLICIT_PATTERN.test(normalized);
  const implicitChart =
    CHART_ANALYSIS_PATTERN.test(normalized) &&
    (CHART_GROUPING_PATTERN.test(normalized) ||
      /\bmonth[-\s]?over[-\s]?month|week[-\s]?over[-\s]?week|year[-\s]?over[-\s]?year\b/i.test(
        normalized,
      ));
  const explicitSpreadsheet = SPREADSHEET_EXPLICIT_PATTERN.test(normalized);
  const implicitSpreadsheet =
    SPREADSHEET_HANDOFF_PATTERN.test(normalized) &&
    (SPREADSHEET_ACTION_PATTERN.test(normalized) ||
      SPREADSHEET_EXPORT_PATTERN.test(normalized));
  const wantsChart = explicitChart || implicitChart;
  const wantsSpreadsheet = explicitSpreadsheet || implicitSpreadsheet;

  if (!wantsChart && !wantsSpreadsheet) {
    return null;
  }
  if (wantsChart && wantsSpreadsheet) {
    return SPREADSHEET_EXPORT_PATTERN.test(normalized)
      ? "artifact.spreadsheet.generate"
      : "artifact.chart.generate";
  }
  return wantsSpreadsheet
    ? "artifact.spreadsheet.generate"
    : "artifact.chart.generate";
};

const resolveArtifactTaskProposal = (input: {
  pathname: string;
  question: string;
}): AssistantTaskProposal | null => {
  const workflowKey = inferArtifactWorkflowKey(input.question);
  if (!workflowKey) {
    return null;
  }
  const support = resolveAssistantArtifactSupport({
    pathname: input.pathname,
    workflowKey,
    question: input.question,
  });
  if (!support.supported && support.fallbackToChat) {
    return null;
  }
  if (workflowKey === "artifact.visualization3d.generate") {
    const isTuitionVisualization = isTuitionPriorYearVisualizationPrompt(
      input.question,
    );
    return {
      workflowKey,
      taskClass: "artifact_generate",
      title: isTuitionVisualization
        ? "Tuition prior-year 3D visualization"
        : "Advanced Three.js visualization",
      summary: isTuitionVisualization
        ? "Generate an interactive tuition payment visualization with current-year and prior-year layers in the background."
        : "Generate an interactive 3D admissions artifact and inline preview in the background.",
      autoStart: true,
    };
  }
  return workflowKey === "artifact.chart.generate"
    ? {
        workflowKey,
        taskClass: "artifact_generate",
        title: "Admissions status chart",
        summary: "Generate an inline chart preview in the background.",
        autoStart: true,
      }
    : {
        workflowKey,
        taskClass: "artifact_generate",
        title: "Spreadsheet export",
        summary:
          "Generate a spreadsheet artifact in the background while chat stays live.",
        autoStart: true,
      };
};

export const detectAssistantTaskIntent = (input: {
  question: string;
  pathname: string;
  context: AdmissionsAIContext;
  spreadsheetSession?: {
    workbookArtifactPath: string;
    title?: string | null;
    activeSheetName?: string | null;
    visibleRowCount?: number | null;
    totalRowCount?: number | null;
    activeFilters?: string[];
  } | null;
}): AssistantTaskProposal | null => {
  const question = input.question.trim();
  if (!question) {
    return null;
  }
  const normalized = question.toLowerCase();
  const familyId = extractFamilyIdFromPath(input.pathname);
  const recipientEmail = extractEmail(question);
  const academicYear = normalizeAcademicYear(input.context);
  const spreadsheetSessionPath = toText(
    input.spreadsheetSession?.workbookArtifactPath,
  );
  const spreadsheetSessionTitle = toText(input.spreadsheetSession?.title);

  if (spreadsheetSessionPath && isSpreadsheetModificationPrompt(question)) {
    return {
      workflowKey: "artifact.spreadsheet.modify",
      taskClass: "artifact_generate",
      title: "Update spreadsheet",
      summary: spreadsheetSessionTitle
        ? `Revise ${spreadsheetSessionTitle} in the background while chat stays live.`
        : "Revise the current spreadsheet in the background while chat stays live.",
    };
  }

  if (
    /\b(landing page|donation page|event page|campaign page)\b/i.test(
      normalized,
    )
  ) {
    return {
      workflowKey: "artifact.landing_page.draft",
      taskClass: "artifact_generate",
      title: "Landing page draft",
      summary:
        "Generate a standalone landing page draft artifact in the background.",
    };
  }

  const artifactTaskProposal = resolveArtifactTaskProposal({
    pathname: input.pathname,
    question,
  });
  if (artifactTaskProposal) {
    return artifactTaskProposal;
  }

  if (/\b(diagram|flowchart|mermaid)\b/i.test(normalized)) {
    return {
      workflowKey: "artifact.diagram.generate",
      taskClass: "artifact_generate",
      title: "Diagram draft",
      summary: "Generate a diagram artifact in the background.",
    };
  }

  if (
    /\b(pending-payment|pending payment|open balance report)\b/i.test(
      normalized,
    ) ||
    (/\brevenue\b/i.test(normalized) &&
      /\b(report|export|calculate|package|generate)\b/i.test(normalized))
  ) {
    return {
      workflowKey: "finance.report.pending_payment_revenue",
      taskClass: "compute_report",
      title: "Pending-payment revenue report",
      summary:
        "Calculate and package a pending-payment revenue report in the background.",
      familyId,
      academicYear,
    };
  }

  if (
    /\b(send (an |the )?invoice|invoice (this )?family|email (an |the )?invoice|deliver (an |the )?invoice)\b/i.test(
      normalized,
    )
  ) {
    const hasRecipientContext = Boolean(recipientEmail || familyId);
    return {
      workflowKey: "finance.invoice.send",
      taskClass: "external_side_effect",
      title: "Invoice delivery",
      summary: hasRecipientContext
        ? "Prepare an invoice preview first, then require explicit confirmation before email delivery."
        : "Invoice delivery needs either a family route context or an explicit recipient email.",
      recipientEmail,
      familyId,
      academicYear,
      requiresFollowupConfirmation: true,
      disabledReason: hasRecipientContext
        ? undefined
        : "Open a family route or include a recipient email before launching invoice delivery.",
    };
  }

  return null;
};
